import { useEffect, useState, useCallback } from 'react';
import {
  Building2,
  Layers,
  Train,
  Plus,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  Check,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useOrgHierarchyStore } from '../../store/useOrgHierarchyStore';
import type { Organization, Program, ReleaseTrain } from '../../types';

type EditTarget =
  | { kind: 'org'; id: string | null }
  | { kind: 'program'; id: string | null; orgId: string }
  | { kind: 'train'; id: string | null; programId: string }
  | null;

export default function OrgAdminPanel() {
  const {
    organizations,
    programs,
    releaseTrains,
    loading,
    fetchOrganizations,
    fetchPrograms,
    fetchReleaseTrains,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    createProgram,
    updateProgram,
    deleteProgram,
    createReleaseTrain,
    updateReleaseTrain,
    deleteReleaseTrain,
  } = useOrgHierarchyStore();

  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const [editName, setEditName] = useState('');
  const [editParentOrgId, setEditParentOrgId] = useState('');

  useEffect(() => {
    fetchOrganizations();
    fetchPrograms();
    fetchReleaseTrains();
  }, [fetchOrganizations, fetchPrograms, fetchReleaseTrains]);

  const toggleOrg = useCallback((id: string) => {
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleProgram = useCallback((id: string) => {
    setExpandedPrograms((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const startAdd = (kind: string, parentId?: string) => {
    setEditName('');
    setEditParentOrgId('');
    if (kind === 'org') setEditTarget({ kind: 'org', id: null });
    else if (kind === 'program') setEditTarget({ kind: 'program', id: null, orgId: parentId! });
    else if (kind === 'train') setEditTarget({ kind: 'train', id: null, programId: parentId! });
  };

  const startEdit = (kind: string, item: Organization | Program | ReleaseTrain) => {
    setEditName(item.name);
    setEditParentOrgId((item as any).parentOrgId || '');
    if (kind === 'org') setEditTarget({ kind: 'org', id: item._id });
    else if (kind === 'program') setEditTarget({ kind: 'program', id: item._id, orgId: (item as Program).organizationId });
    else if (kind === 'train') setEditTarget({ kind: 'train', id: item._id, programId: (item as ReleaseTrain).programId });
  };

  const cancelEdit = () => {
    setEditTarget(null);
    setEditName('');
  };

  const saveEdit = async () => {
    if (!editTarget || !editName.trim()) return;
    try {
      if (editTarget.kind === 'org') {
        if (editTarget.id) {
          await updateOrganization(editTarget.id, { name: editName.trim(), parentOrgId: editParentOrgId });
          toast.success('Organization updated');
        } else {
          await createOrganization({ name: editName.trim(), parentOrgId: editParentOrgId });
          toast.success('Organization created');
        }
      } else if (editTarget.kind === 'program') {
        if (editTarget.id) {
          await updateProgram(editTarget.id, { name: editName.trim() });
          toast.success('Program updated');
        } else {
          await createProgram({ name: editName.trim(), organizationId: editTarget.orgId });
          toast.success('Program created');
          setExpandedOrgs((prev) => new Set(prev).add(editTarget.orgId));
        }
      } else if (editTarget.kind === 'train') {
        if (editTarget.id) {
          await updateReleaseTrain(editTarget.id, { name: editName.trim() });
          toast.success('Release train updated');
        } else {
          await createReleaseTrain({ name: editName.trim(), programId: editTarget.programId });
          toast.success('Release train created');
          setExpandedPrograms((prev) => new Set(prev).add(editTarget.programId));
        }
      }
      cancelEdit();
    } catch {
      toast.error('Save failed');
    }
  };

  const handleDelete = async (kind: string, id: string, name: string) => {
    if (!confirm(`Delete "${name}"? This will cascade to child items.`)) return;
    try {
      if (kind === 'org') await deleteOrganization(id);
      else if (kind === 'program') await deleteProgram(id);
      else if (kind === 'train') await deleteReleaseTrain(id);
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  const InlineEditor = () => (
    <div className="px-2 py-1">
      <div className="flex items-center gap-1">
        <input
          autoFocus
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 text-sm px-2 py-1 rounded border border-accent/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
          placeholder="Name..."
        />
        <button onClick={saveEdit} className="p-1 text-green-500 hover:text-green-600"><Check size={14} /></button>
        <button onClick={cancelEdit} className="p-1 text-gray-400 hover:text-gray-600"><X size={14} /></button>
      </div>
      {editTarget?.kind === 'org' && (
        <select
          value={editParentOrgId}
          onChange={(e) => setEditParentOrgId(e.target.value)}
          className="mt-1 w-full text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          title="Parent organization (for nested / partner orgs)"
        >
          <option value="">Top-level organization</option>
          {organizations
            .filter((o) => o._id !== editTarget.id)
            .map((o) => <option key={o._id} value={o._id}>Under: {o.name}</option>)}
        </select>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-3 text-sm">
      {/* Header + Add Org button */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Hierarchy
        </span>
        <button
          onClick={() => startAdd('org')}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-accent"
          title="Add Organization"
        >
          <Plus size={14} />
        </button>
      </div>

      {/* Inline add org */}
      {editTarget?.kind === 'org' && editTarget.id === null && <InlineEditor />}

      {organizations.length === 0 && !editTarget && (
        <p className="text-gray-400 dark:text-gray-500 text-xs px-2">No organizations yet</p>
      )}

      {/* Org tree */}
      <div className="space-y-0.5">
        {organizations.map((org) => {
          const isExpanded = expandedOrgs.has(org._id);
          const orgPrograms = programs.filter((p) => p.organizationId === org._id);

          return (
            <div key={org._id}>
              {/* Org row */}
              <div className="group flex items-center gap-1 px-1 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-750">
                <button onClick={() => toggleOrg(org._id)} className="p-0.5 text-gray-400">
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <Building2 size={14} className="text-indigo-500 shrink-0" />

                {editTarget?.kind === 'org' && editTarget.id === org._id ? (
                  <div className="flex-1 flex items-center gap-1">
                    <input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="flex-1 text-sm px-1 py-0.5 rounded border border-accent/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none"
                    />
                    <button onClick={saveEdit} className="p-0.5 text-green-500"><Check size={12} /></button>
                    <button onClick={cancelEdit} className="p-0.5 text-gray-400"><X size={12} /></button>
                  </div>
                ) : (
                  <>
                    <span
                      className="flex-1 text-gray-800 dark:text-gray-200 truncate cursor-pointer"
                      onClick={() => toggleOrg(org._id)}
                    >
                      {org.displayId && <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mr-1">{org.displayId}</span>}
                      {org.name}
                    </span>
                    <span className="text-xs text-gray-400 mr-1">{orgPrograms.length}</span>
                    <div className="hidden group-hover:flex items-center gap-0.5">
                      <button onClick={() => startAdd('program', org._id)} className="p-0.5 text-gray-400 hover:text-accent" title="Add Program"><Plus size={12} /></button>
                      <button onClick={() => startEdit('org', org)} className="p-0.5 text-gray-400 hover:text-accent" title="Rename"><Pencil size={12} /></button>
                      <button onClick={() => handleDelete('org', org._id, org.name)} className="p-0.5 text-gray-400 hover:text-red-500" title="Delete"><Trash2 size={12} /></button>
                    </div>
                  </>
                )}
              </div>

              {/* Programs under org */}
              {isExpanded && (
                <div className="ml-5 border-l border-gray-200 dark:border-gray-700">
                  {/* Inline add program */}
                  {editTarget?.kind === 'program' && editTarget.id === null && editTarget.orgId === org._id && (
                    <div className="ml-2"><InlineEditor /></div>
                  )}

                  {orgPrograms.map((prog) => {
                    const isProgramExpanded = expandedPrograms.has(prog._id);
                    const progTrains = releaseTrains.filter((t) => t.programId === prog._id);

                    return (
                      <div key={prog._id}>
                        {/* Program row */}
                        <div className="group flex items-center gap-1 px-1 py-1.5 ml-2 rounded hover:bg-gray-50 dark:hover:bg-gray-750">
                          <button onClick={() => toggleProgram(prog._id)} className="p-0.5 text-gray-400">
                            {isProgramExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </button>
                          <Layers size={13} className="text-blue-500 shrink-0" />

                          {editTarget?.kind === 'program' && editTarget.id === prog._id ? (
                            <div className="flex-1 flex items-center gap-1">
                              <input
                                autoFocus
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="flex-1 text-sm px-1 py-0.5 rounded border border-accent/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none"
                              />
                              <button onClick={saveEdit} className="p-0.5 text-green-500"><Check size={12} /></button>
                              <button onClick={cancelEdit} className="p-0.5 text-gray-400"><X size={12} /></button>
                            </div>
                          ) : (
                            <>
                              <span
                                className="flex-1 text-gray-700 dark:text-gray-300 truncate cursor-pointer"
                                onClick={() => toggleProgram(prog._id)}
                              >
                                {prog.displayId && <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mr-1">{prog.displayId}</span>}
                                {prog.name}
                              </span>
                              <span className="text-xs text-gray-400 mr-1">{progTrains.length}</span>
                              <div className="hidden group-hover:flex items-center gap-0.5">
                                <button onClick={() => startAdd('train', prog._id)} className="p-0.5 text-gray-400 hover:text-accent" title="Add Release Train"><Plus size={12} /></button>
                                <button onClick={() => startEdit('program', prog)} className="p-0.5 text-gray-400 hover:text-accent" title="Rename"><Pencil size={12} /></button>
                                <button onClick={() => handleDelete('program', prog._id, prog.name)} className="p-0.5 text-gray-400 hover:text-red-500" title="Delete"><Trash2 size={12} /></button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Release trains under program */}
                        {isProgramExpanded && (
                          <div className="ml-7 border-l border-gray-200 dark:border-gray-700">
                            {/* Inline add train */}
                            {editTarget?.kind === 'train' && editTarget.id === null && editTarget.programId === prog._id && (
                              <div className="ml-2"><InlineEditor /></div>
                            )}

                            {progTrains.map((train) => (
                              <div key={train._id} className="group flex items-center gap-1 px-1 py-1.5 ml-2 rounded hover:bg-gray-50 dark:hover:bg-gray-750">
                                <Train size={12} className="text-emerald-500 shrink-0 ml-3" />

                                {editTarget?.kind === 'train' && editTarget.id === train._id ? (
                                  <div className="flex-1 flex items-center gap-1">
                                    <input
                                      autoFocus
                                      value={editName}
                                      onChange={(e) => setEditName(e.target.value)}
                                      onKeyDown={handleKeyDown}
                                      className="flex-1 text-sm px-1 py-0.5 rounded border border-accent/50 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none"
                                    />
                                    <button onClick={saveEdit} className="p-0.5 text-green-500"><Check size={12} /></button>
                                    <button onClick={cancelEdit} className="p-0.5 text-gray-400"><X size={12} /></button>
                                  </div>
                                ) : (
                                  <>
                                    <span className="flex-1 text-gray-600 dark:text-gray-400 truncate">
                                      {train.displayId && <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mr-1">{train.displayId}</span>}
                                      {train.name}
                                    </span>
                                    <div className="hidden group-hover:flex items-center gap-0.5">
                                      <button onClick={() => startEdit('train', train)} className="p-0.5 text-gray-400 hover:text-accent" title="Rename"><Pencil size={12} /></button>
                                      <button onClick={() => handleDelete('train', train._id, train.name)} className="p-0.5 text-gray-400 hover:text-red-500" title="Delete"><Trash2 size={12} /></button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}

                            {progTrains.length === 0 && !(editTarget?.kind === 'train' && editTarget.programId === prog._id) && (
                              <p className="text-xs text-gray-400 px-3 py-1 ml-2">No release trains</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {orgPrograms.length === 0 && !(editTarget?.kind === 'program' && editTarget.orgId === org._id) && (
                    <p className="text-xs text-gray-400 px-3 py-1 ml-2">No programs</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Program Increments and Releases moved out to Release Management when
          the rails were grouped: cadence generates releases, so both belong in
          one place. See ProgramIncrementsPanel, the Releases page, and
          docs/devops_release_management_plan.md Part 12. Org Admin is now
          purely structural. */}
    </div>
  );
}
